import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Robot, Plus, Trash, CircleNotch, Play, Stop, ArrowsClockwise, FloppyDisk, ClockCounterClockwise, Terminal, Gear, Brain, WarningCircle, CheckCircle, X } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#229ED9';

const StatusPill = ({ s }) => {
  const map = {
    running: ['Running', '#39FF14'],
    deploying: ['Deploying…', '#FFB020'],
    crashed: ['Crashed', '#FF4444'],
    stopped: ['Stopped', '#8892a0'],
    not_deployed: ['Not deployed', '#8892a0'],
  };
  const [label, color] = map[s] || [s || '—', '#8892a0'];
  return <span data-testid="dcbot-status-pill" className="px-2 py-1 text-xs font-bold border" style={{ color, borderColor: color + '55' }}>{label}</span>;
};

const DCBotRunner = () => {
  const [bots, setBots] = useState([]);
  const [doAccounts, setDoAccounts] = useState([]);
  const [selId, setSelId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('status');
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState('');
  const [knowledge, setKnowledge] = useState({ content: '', version: 0, versions: [] });
  const [knEdit, setKnEdit] = useState('');
  const [knNote, setKnNote] = useState('');
  const [cfg, setCfg] = useState({ model: '', owner_id: '', bot_token: '', anthropic_api_key: '' });
  const [form, setForm] = useState({ label: '', do_account_id: '', bot_token: '', anthropic_api_key: '', owner_id: '', model: 'claude-sonnet-5' });

  const sel = bots.find(b => b.id === selId) || null;

  const load = useCallback(async () => {
    try {
      const [b, a] = await Promise.all([
        axios.get(`${API}/api/dc-bots`, { withCredentials: true }),
        axios.get(`${API}/api/accounts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setBots(b.data || []);
      setDoAccounts(a.data || []);
    } catch { }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openBot = (b) => {
    setSelId(b.id); setTab('status'); setStatus(null); setLogs('');
    setCfg({ model: b.model || '', owner_id: b.owner_id || '', bot_token: '', anthropic_api_key: '' });
    refreshStatus(b.id);
    loadKnowledge(b.id);
  };

  const refreshStatus = async (id) => {
    try {
      const r = await axios.get(`${API}/api/dc-bots/${id}/status`, { withCredentials: true });
      setStatus(r.data);
      setBots(prev => prev.map(x => x.id === id ? { ...x, deploy_status: r.data.deploy_status } : x));
    } catch (e) { setStatus({ deploy_status: 'unknown' }); }
  };

  const loadKnowledge = async (id) => {
    try {
      const r = await axios.get(`${API}/api/dc-bots/${id}/knowledge`, { withCredentials: true });
      setKnowledge(r.data); setKnEdit(r.data.content); setKnNote('');
    } catch { }
  };

  const loadLogs = async (id) => {
    setBusy('logs');
    try {
      const r = await axios.get(`${API}/api/dc-bots/${id}/logs`, { withCredentials: true });
      setLogs(r.data.text || '(empty)');
    } catch (e) { setLogs('Error: ' + (e.response?.data?.detail || 'could not fetch logs')); } finally { setBusy(''); }
  };

  const create = async () => {
    if (!form.label || !form.do_account_id || !form.bot_token || !form.anthropic_api_key || !form.owner_id) {
      return toast.error('All fields except model are required');
    }
    setBusy('create');
    try {
      const r = await axios.post(`${API}/api/dc-bots`, form, { withCredentials: true });
      toast.success('Bot created — deploy it when ready');
      setShowCreate(false);
      setForm({ label: '', do_account_id: '', bot_token: '', anthropic_api_key: '', owner_id: '', model: 'claude-sonnet-5' });
      await load();
      openBot(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); } finally { setBusy(''); }
  };

  const act = async (path, label, method = 'post', body = {}) => {
    if (!sel) return;
    setBusy(label);
    try {
      await axios[method](`${API}/api/dc-bots/${sel.id}${path}`, method === 'get' ? { withCredentials: true } : body, method === 'get' ? undefined : { withCredentials: true });
      toast.success(`${label} — done`);
      await load(); refreshStatus(sel.id);
    } catch (e) { toast.error(e.response?.data?.detail || `${label} failed`); } finally { setBusy(''); }
  };

  const deploy = () => act('/deploy', 'Deploy', 'post', { public_base_url: API });
  const restart = () => act('/restart', 'Restart');
  const stop = () => { if (window.confirm('Stop tears down the DO app (stops billing). Continue?')) act('/stop', 'Stop'); };
  const delBot = async () => {
    if (!sel || !window.confirm('Delete this bot and its DO app permanently?')) return;
    setBusy('del');
    try {
      await axios.delete(`${API}/api/dc-bots/${sel.id}`, { withCredentials: true });
      toast.success('Deleted'); setSelId(null); await load();
    } catch (e) { toast.error('Failed'); } finally { setBusy(''); }
  };

  const saveConfig = async () => {
    setBusy('cfg');
    const payload = {};
    if (cfg.model) payload.model = cfg.model;
    if (cfg.owner_id) payload.owner_id = cfg.owner_id;
    if (cfg.bot_token) payload.bot_token = cfg.bot_token;
    if (cfg.anthropic_api_key) payload.anthropic_api_key = cfg.anthropic_api_key;
    try {
      await axios.patch(`${API}/api/dc-bots/${sel.id}`, payload, { withCredentials: true });
      toast.success('Config saved — Restart to apply on the running bot');
      setCfg({ ...cfg, bot_token: '', anthropic_api_key: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };

  const saveKnowledge = async () => {
    setBusy('kn');
    try {
      const r = await axios.put(`${API}/api/dc-bots/${sel.id}/knowledge`, { content: knEdit, note: knNote, restart: true, public_base_url: API }, { withCredentials: true });
      toast.success(r.data.restarted ? `Saved v${r.data.version} & restarting bot` : `Saved v${r.data.version}`);
      loadKnowledge(sel.id); refreshStatus(sel.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };

  const rollback = async (v) => {
    if (!window.confirm(`Roll back to knowledge v${v}? (creates a new version & restarts the bot)`)) return;
    setBusy('rb' + v);
    try {
      const r = await axios.post(`${API}/api/dc-bots/${sel.id}/knowledge/rollback/${v}`, {}, { withCredentials: true });
      toast.success(`Rolled back — now v${r.data.version}`);
      loadKnowledge(sel.id); refreshStatus(sel.id);
    } catch (e) { toast.error('Failed'); } finally { setBusy(''); }
  };

  const Field = ({ k, ph, type = 'text' }) => (
    <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph} type={type}
      data-testid={`dcbot-form-${k}`} className="bg-[#111318] border border-white/10 p-2.5 text-white text-sm focus:outline-none w-full" style={{ caretColor: T }} />
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black mb-2 flex items-center gap-3" style={{ color: T }}><Robot size={36} weight="fill" /> BOT RUNNER</h1>
          <p className="text-slate-400 font-mono">Always-on AI Telegram bots on DigitalOcean App Platform. Deploy, watch, and edit their brain live.</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} data-testid="dcbot-new-btn" className="flex items-center gap-2 text-white px-4 py-2.5 font-bold text-sm uppercase tracking-wider" style={{ background: T }}><Plus size={16} /> New Bot</button>
      </div>

      {showCreate && (
        <div className="bg-card border border-white/10 p-6 space-y-4 max-w-2xl" data-testid="dcbot-create-form">
          <div className="flex justify-between items-center"><h3 className="font-bold uppercase tracking-wider">New Telegram Bot</h3><button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white"><X size={18} /></button></div>
          <Field k="label" ph="Label (e.g. Dial Clear Support)" />
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-1">DigitalOcean Account</label>
            <select value={form.do_account_id} onChange={e => setForm({ ...form, do_account_id: e.target.value })} data-testid="dcbot-form-do_account_id" className="bg-[#111318] border border-white/10 p-2.5 text-white text-sm w-full">
              <option value="">-- pick a saved DO account --</option>
              {doAccounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
          <Field k="bot_token" ph="Telegram BOT_TOKEN (from @BotFather)" type="password" />
          <Field k="anthropic_api_key" ph="ANTHROPIC_API_KEY (sk-ant-…)" type="password" />
          <Field k="owner_id" ph="OWNER_ID (your numeric Telegram id from @userinfobot)" />
          <Field k="model" ph="Model (default claude-sonnet-5)" />
          <p className="text-xs text-slate-500 font-mono">Secrets are encrypted at rest and set as DigitalOcean SECRET env vars. Never logged.</p>
          <button onClick={create} disabled={busy === 'create'} data-testid="dcbot-create-submit" className="flex items-center gap-2 text-white px-4 py-2.5 font-bold text-sm uppercase tracking-wider disabled:opacity-50" style={{ background: T }}>{busy === 'create' ? <CircleNotch className="animate-spin" size={16} /> : <Plus size={16} />} Create Bot</button>
        </div>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Bot list */}
        <div className="space-y-3">
          {bots.length === 0 && <div className="text-slate-500 border border-dashed border-white/15 p-6 text-center text-sm">No bots yet. Create one to get started.</div>}
          {bots.map(b => (
            <div key={b.id} onClick={() => openBot(b)} data-testid={`dcbot-card-${b.id}`}
              className={`cursor-pointer bg-card border p-4 transition-colors ${selId === b.id ? '' : 'border-white/10 hover:border-white/25'}`}
              style={selId === b.id ? { borderColor: T } : {}}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold text-white truncate">{b.label}</div>
                <StatusPill s={b.deploy_status} />
              </div>
              <div className="text-xs text-slate-500 font-mono mt-1">{b.model} · knowledge v{b.knowledge_version}</div>
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {sel ? (
          <div className="bg-card border border-white/10" data-testid="dcbot-detail">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div><div className="text-lg font-black text-white">{sel.label}</div><div className="text-xs text-slate-500 font-mono">{sel.app_name || 'not deployed'}</div></div>
              <button onClick={delBot} disabled={busy === 'del'} data-testid="dcbot-delete-btn" className="flex items-center gap-1 bg-red-500/10 text-red-400 border border-red-500/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-red-500 hover:text-white"><Trash size={14} /> Delete</button>
            </div>

            {/* tabs */}
            <div className="flex border-b border-white/10 text-sm font-bold uppercase tracking-wider">
              {[['status', 'Status', Gear], ['knowledge', 'Knowledge', Brain], ['logs', 'Logs', Terminal], ['config', 'Config', Gear]].map(([id, lbl]) => (
                <button key={id} onClick={() => { setTab(id); if (id === 'logs') loadLogs(sel.id); }} data-testid={`dcbot-tab-${id}`}
                  className={`px-4 py-3 transition-colors ${tab === id ? 'text-white' : 'text-slate-500 hover:text-white'}`}
                  style={tab === id ? { borderBottom: `2px solid ${T}` } : {}}>{lbl}</button>
              ))}
            </div>

            <div className="p-6">
              {tab === 'status' && (
                <div className="space-y-5">
                  <div className="flex items-center gap-3">
                    <StatusPill s={status?.deploy_status || sel.deploy_status} />
                    <button onClick={() => refreshStatus(sel.id)} className="text-slate-400 hover:text-white flex items-center gap-1 text-xs" data-testid="dcbot-refresh-status"><ArrowsClockwise size={14} /> Refresh</button>
                  </div>
                  {status?.conflict_detected && (
                    <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-300 p-3 text-sm" data-testid="dcbot-conflict-warn">
                      <WarningCircle size={18} /> Telegram <b>Conflict</b> detected — two pollers may be running. Ensure only one instance is up.
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-4 text-sm font-mono">
                    <Info label="Model" value={sel.model} />
                    <Info label="Knowledge version" value={`v${sel.knowledge_version}`} />
                    <Info label="BOT_TOKEN" value={sel.bot_token_masked} />
                    <Info label="ANTHROPIC_API_KEY" value={sel.anthropic_key_masked} />
                    <Info label="OWNER_ID" value={sel.owner_id} />
                    <Info label="Deployment phase" value={status?.phase || '—'} />
                  </div>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <Btn testid="dcbot-deploy-btn" onClick={deploy} busy={busy === 'Deploy'} icon={Play} label={sel.do_app_id ? 'Redeploy' : 'Deploy'} primary />
                    <Btn testid="dcbot-restart-btn" onClick={restart} busy={busy === 'Restart'} icon={ArrowsClockwise} label="Restart" disabled={!sel.do_app_id} />
                    <Btn testid="dcbot-stop-btn" onClick={stop} busy={busy === 'Stop'} icon={Stop} label="Stop" danger disabled={!sel.do_app_id} />
                  </div>
                </div>
              )}

              {tab === 'knowledge' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-400 font-mono">Editing brain — current v{knowledge.version}. Save & Restart applies within ~30s.</div>
                  </div>
                  <textarea value={knEdit} onChange={e => setKnEdit(e.target.value)} data-testid="dcbot-knowledge-editor" spellCheck={false}
                    className="w-full h-80 bg-[#0A0B0E] border border-white/10 p-3 text-slate-200 text-xs font-mono focus:outline-none" style={{ caretColor: T }} />
                  <div className="flex flex-wrap items-center gap-3">
                    <input value={knNote} onChange={e => setKnNote(e.target.value)} placeholder="Change note (optional)" data-testid="dcbot-knowledge-note" className="bg-[#111318] border border-white/10 p-2 text-white text-sm flex-1 min-w-[200px]" />
                    <button onClick={saveKnowledge} disabled={busy === 'kn'} data-testid="dcbot-knowledge-save" className="flex items-center gap-2 text-white px-4 py-2 font-bold text-sm uppercase tracking-wider disabled:opacity-50" style={{ background: T }}>{busy === 'kn' ? <CircleNotch className="animate-spin" size={16} /> : <FloppyDisk size={16} />} Save & Restart</button>
                  </div>
                  <div className="border-t border-white/10 pt-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center gap-2"><ClockCounterClockwise size={14} /> Version history</div>
                    <div className="space-y-1 max-h-48 overflow-auto">
                      {knowledge.versions.map(v => (
                        <div key={v.version} className="flex items-center justify-between text-xs font-mono py-1.5 border-b border-white/5" data-testid={`dcbot-knowledge-version-${v.version}`}>
                          <span className="text-slate-300">v{v.version} {v.note ? `· ${v.note}` : ''}</span>
                          <button onClick={() => rollback(v.version)} disabled={busy === 'rb' + v.version} className="text-slate-400 hover:text-white flex items-center gap-1" data-testid={`dcbot-rollback-${v.version}`}><ClockCounterClockwise size={12} /> Rollback</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {tab === 'logs' && (
                <div className="space-y-3">
                  <button onClick={() => loadLogs(sel.id)} disabled={busy === 'logs'} data-testid="dcbot-logs-refresh" className="flex items-center gap-1 text-slate-400 hover:text-white text-xs"><ArrowsClockwise size={14} /> Refresh logs</button>
                  <pre data-testid="dcbot-logs" className="w-full h-96 overflow-auto bg-[#0A0B0E] border border-white/10 p-3 text-slate-300 text-xs font-mono whitespace-pre-wrap">{busy === 'logs' ? 'Loading…' : (logs || 'Click refresh to load logs. (Secrets are redacted.)')}</pre>
                </div>
              )}

              {tab === 'config' && (
                <div className="space-y-4 max-w-lg">
                  <p className="text-sm text-slate-400 font-mono">Update config/secrets. Restart the bot to apply changes on the running worker.</p>
                  <L label="Model"><input value={cfg.model} onChange={e => setCfg({ ...cfg, model: e.target.value })} data-testid="dcbot-cfg-model" className="bg-[#111318] border border-white/10 p-2.5 text-white text-sm w-full" /></L>
                  <L label="OWNER_ID"><input value={cfg.owner_id} onChange={e => setCfg({ ...cfg, owner_id: e.target.value })} data-testid="dcbot-cfg-owner" className="bg-[#111318] border border-white/10 p-2.5 text-white text-sm w-full" /></L>
                  <L label="BOT_TOKEN (leave blank to keep)"><input type="password" value={cfg.bot_token} onChange={e => setCfg({ ...cfg, bot_token: e.target.value })} data-testid="dcbot-cfg-bottoken" placeholder={sel.bot_token_masked} className="bg-[#111318] border border-white/10 p-2.5 text-white text-sm w-full" /></L>
                  <L label="ANTHROPIC_API_KEY (leave blank to keep)"><input type="password" value={cfg.anthropic_api_key} onChange={e => setCfg({ ...cfg, anthropic_api_key: e.target.value })} data-testid="dcbot-cfg-anthropic" placeholder={sel.anthropic_key_masked} className="bg-[#111318] border border-white/10 p-2.5 text-white text-sm w-full" /></L>
                  <button onClick={saveConfig} disabled={busy === 'cfg'} data-testid="dcbot-cfg-save" className="flex items-center gap-2 text-white px-4 py-2.5 font-bold text-sm uppercase tracking-wider disabled:opacity-50" style={{ background: T }}>{busy === 'cfg' ? <CircleNotch className="animate-spin" size={16} /> : <FloppyDisk size={16} />} Save Config</button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-card border border-dashed border-white/15 flex items-center justify-center text-slate-500 min-h-[300px]">Select a bot to manage it.</div>
        )}
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div><div className="text-[10px] uppercase tracking-widest text-slate-600">{label}</div><div className="text-slate-200 break-all">{value || '—'}</div></div>
);
const L = ({ label, children }) => (
  <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-1">{label}</label>{children}</div>
);
const Btn = ({ onClick, busy, icon: Icon, label, primary, danger, disabled, testid }) => (
  <button onClick={onClick} disabled={busy || disabled} data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm uppercase tracking-wider disabled:opacity-40 ${danger ? 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500 hover:text-white' : primary ? 'text-white' : 'border border-white/20 text-white hover:bg-white/10'}`}
    style={primary ? { background: T } : {}}>
    {busy ? <CircleNotch className="animate-spin" size={16} /> : <Icon size={16} />} {label}
  </button>
);

export default DCBotRunner;
